"use client"

import { useState, useEffect, useCallback, useRef } from 'react'

// Define Tetrominos with neon colors matching Pink Pixel OS theme
const TETROMINOS = {
  I: { shape: [[1, 1, 1, 1]], color: "#00e1ff" }, // neon-blue
  J: {
    shape: [
      [1, 0, 0],
      [1, 1, 1],
    ],
    color: "#0099ff", // blue-500
  },
  L: {
    shape: [
      [0, 0, 1],
      [1, 1, 1],
    ],
    color: "#ff9900", // neon-orange
  },
  O: {
    shape: [
      [1, 1],
      [1, 1],
    ],
    color: "#ffff00", // neon-yellow
  },
  S: {
    shape: [
      [0, 1, 1],
      [1, 1, 0],
    ],
    color: "#39ff14", // neon-green
  },
  T: {
    shape: [
      [0, 1, 0],
      [1, 1, 1],
    ],
    color: "#bf00ff", // neon-purple
  },
  Z: {
    shape: [
      [1, 1, 0],
      [0, 1, 1],
    ],
    color: "#ff2b6e", // neon-red
  },
}

const BOARD_WIDTH = 10
const BOARD_HEIGHT = 20
const INITIAL_DROP_TIME = 800
const SPEED_INCREASE_FACTOR = 0.95

const createEmptyBoard = () => Array.from({ length: BOARD_HEIGHT }, () => Array(BOARD_WIDTH).fill(0))

const randomTetromino = () => {
  const keys = Object.keys(TETROMINOS)
  const randKey = keys[Math.floor(Math.random() * keys.length)]
  return TETROMINOS[randKey]
}

export default function TetrisApp() {
  const [board, setBoard] = useState(createEmptyBoard())
  const [currentPiece, setCurrentPiece] = useState(null)
  const [score, setScore] = useState(0)
  const [gameOver, setGameOver] = useState(false)
  const [dropTime, setDropTime] = useState(INITIAL_DROP_TIME)
  const [level, setLevel] = useState(1)
  const [isMusicPlaying, setIsMusicPlaying] = useState(false)
  const [completedRows, setCompletedRows] = useState([])
  const audioRef = useRef(null)
  const dropInterval = useRef(null)

  const checkCollision = (x, y, shape) => {
    for (let row = 0; row < shape.length; row++) {
      for (let col = 0; col < shape[row].length; col++) {
        if (shape[row][col] !== 0) {
          const newX = x + col
          const newY = y + row
          if (newX < 0 || newX >= BOARD_WIDTH || newY >= BOARD_HEIGHT || (newY >= 0 && board[newY][newX] !== 0)) {
            return true
          }
        }
      }
    }
    return false
  }

  const isValidMove = (x, y, shape) => !checkCollision(x, y, shape)

  const moveLeft = useCallback(() => {
    if (currentPiece && isValidMove(currentPiece.x - 1, currentPiece.y, currentPiece.tetromino.shape)) {
      setCurrentPiece((prev) => ({ ...prev, x: prev.x - 1 }))
    }
  }, [currentPiece, board])

  const moveRight = useCallback(() => {
    if (currentPiece && isValidMove(currentPiece.x + 1, currentPiece.y, currentPiece.tetromino.shape)) {
      setCurrentPiece((prev) => ({ ...prev, x: prev.x + 1 }))
    }
  }, [currentPiece, board])

  const moveDown = useCallback(() => {
    if (!currentPiece) return
    if (isValidMove(currentPiece.x, currentPiece.y + 1, currentPiece.tetromino.shape)) {
      setCurrentPiece((prev) => ({ ...prev, y: prev.y + 1 }))
    } else {
      placePiece()
    }
  }, [currentPiece, board])

  const rotate = useCallback(() => {
    if (!currentPiece) return
    const rotated = currentPiece.tetromino.shape[0].map((_, i) =>
      currentPiece.tetromino.shape.map((row) => row[i]).reverse(),
    )
    let newX = currentPiece.x
    let newY = currentPiece.y

    // Try to rotate, if not possible, try to adjust position
    if (!isValidMove(newX, newY, rotated)) {
      // Try to move left
      if (isValidMove(newX - 1, newY, rotated)) {
        newX -= 1
      }
      // Try to move right
      else if (isValidMove(newX + 1, newY, rotated)) {
        newX += 1
      }
      // Try to move up
      else if (isValidMove(newX, newY - 1, rotated)) {
        newY -= 1
      }
      // If still not possible, don't rotate
      else {
        return
      }
    }

    setCurrentPiece((prev) => ({
      ...prev,
      x: newX,
      y: newY,
      tetromino: { ...prev.tetromino, shape: rotated },
    }))
  }, [currentPiece, board])

  const placePiece = useCallback(() => {
    if (!currentPiece) return
    const newBoard = board.map((row) => [...row])
    currentPiece.tetromino.shape.forEach((row, y) => {
      row.forEach((value, x) => {
        if (value !== 0) {
          const boardY = y + currentPiece.y
          const boardX = x + currentPiece.x
          if (boardY >= 0 && boardY < BOARD_HEIGHT && boardX >= 0 && boardX < BOARD_WIDTH) {
            newBoard[boardY][boardX] = currentPiece.tetromino.color
          }
        }
      })
    })
    setBoard(newBoard)
    clearLines(newBoard)
    spawnNewPiece()
  }, [currentPiece, board])

  const clearLines = useCallback(
    (newBoard) => {
      const linesCleared = []
      const updatedBoard = newBoard.filter((row, index) => {
        if (row.every((cell) => cell !== 0)) {
          linesCleared.push(index)
          return false
        }
        return true
      })

      if (linesCleared.length > 0) {
        setCompletedRows(linesCleared)
        setTimeout(() => {
          while (updatedBoard.length < BOARD_HEIGHT) {
            updatedBoard.unshift(Array(BOARD_WIDTH).fill(0))
          }
          setBoard(updatedBoard)
          setCompletedRows([])

          const newScore = score + linesCleared.length * 100
          setScore(newScore)

          if (Math.floor(newScore / 500) > level - 1) {
            setLevel((prev) => prev + 1)
            setDropTime((prev) => prev * SPEED_INCREASE_FACTOR)
          }
        }, 500)
      }
    },
    [score, level],
  )

  const spawnNewPiece = useCallback(() => {
    const newPiece = {
      x: Math.floor(BOARD_WIDTH / 2) - 1,
      y: 0,
      tetromino: randomTetromino(),
    }
    if (checkCollision(newPiece.x, newPiece.y, newPiece.tetromino.shape)) {
      setGameOver(true)
    } else {
      setCurrentPiece(newPiece)
    }
  }, [board])

  useEffect(() => {
    if (!currentPiece && !gameOver) {
      spawnNewPiece()
    }
  }, [currentPiece, gameOver, spawnNewPiece])

  useEffect(() => {
    if (!gameOver) {
      dropInterval.current = setInterval(moveDown, dropTime)
    }
    return () => clearInterval(dropInterval.current)
  }, [moveDown, gameOver, dropTime])

  useEffect(() => {
    const handleKeyPress = (e) => {
      if (gameOver) return
      // Prevent default scrolling behavior for arrow keys
      if (["ArrowLeft", "ArrowRight", "ArrowDown", "ArrowUp"].includes(e.key)) {
        e.preventDefault()
      }

      switch (e.key) {
        case "ArrowLeft":
          moveLeft()
          break
        case "ArrowRight":
          moveRight()
          break
        case "ArrowDown":
          moveDown()
          break
        case "ArrowUp":
          rotate()
          break
        default:
          break
      }
    }
    window.addEventListener("keydown", handleKeyPress)
    return () => window.removeEventListener("keydown", handleKeyPress)
  }, [moveLeft, moveRight, moveDown, rotate, gameOver])

  useEffect(() => {
    const playAudio = async () => {
      if (audioRef.current) {
        audioRef.current.volume = 0.3 // Lower volume for app context
        audioRef.current.loop = true

        if (!gameOver && isMusicPlaying) {
          try {
            const playPromise = audioRef.current.play()
            if (playPromise !== undefined) {
              playPromise.catch((error) => {
                console.error("Audio playback failed:", error)
                setIsMusicPlaying(false)
              })
            }
          } catch (error) {
            console.error("Audio playback error:", error)
            setIsMusicPlaying(false)
          }
        } else {
          if (!audioRef.current.paused) {
            audioRef.current.pause()
          }
        }
      }
    }

    playAudio()
  }, [gameOver, isMusicPlaying])

  const resetGame = () => {
    setBoard(createEmptyBoard())
    setCurrentPiece(null)
    setScore(0)
    setGameOver(false)
    setDropTime(INITIAL_DROP_TIME)
    setLevel(1)
    setCompletedRows([])
    clearInterval(dropInterval.current)
  }

  const renderCell = (x, y) => {
    // For the current falling piece
    if (
      currentPiece &&
      y >= currentPiece.y &&
      y < currentPiece.y + currentPiece.tetromino.shape.length &&
      x >= currentPiece.x &&
      x < currentPiece.x + currentPiece.tetromino.shape[0].length &&
      currentPiece.tetromino.shape[y - currentPiece.y][x - currentPiece.x]
    ) {
      return currentPiece.tetromino.color
    }
    // For placed pieces on the board
    return board[y][x] || "rgba(30, 30, 40, 0.8)"
  }

  const toggleMusic = () => {
    setIsMusicPlaying(!isMusicPlaying)
  }

  return (
    <div className="flex flex-col items-center h-full p-4 bg-gray-900 text-white overflow-y-auto select-none">
      <h1 className="text-2xl sm:text-3xl font-bold mb-3 text-blue-400">
        🎮 Tetris
      </h1>
      
      <div className="flex flex-col lg:flex-row items-start gap-6 pb-4">
        {/* Game Board */}
        <div className="bg-black/70 p-4 rounded-xl shadow-xl border border-pink-500/30">
          <div className="relative">
            <div
              className="grid bg-black/90 relative z-10 border border-gray-600 rounded"
              style={{
                gridTemplateColumns: `repeat(${BOARD_WIDTH}, 1fr)`,
                width: `${BOARD_WIDTH * 25}px`,
                height: `${BOARD_HEIGHT * 25}px`,
              }}
            >
              {board.map((row, y) =>
                row.map((_, x) => (
                  <div
                    key={`${y}-${x}`}
                    className="border border-gray-700/50 transition-all duration-200"
                    style={{
                      width: '25px',
                      height: '25px',
                      backgroundColor: renderCell(x, y),
                      opacity: completedRows.includes(y) ? 0.3 : 1,
                      transform: completedRows.includes(y) ? 'scale(1.1)' : 'scale(1)',
                    }}
                  />
                )),
              )}
            </div>
          </div>
        </div>

        {/* Game Info & Controls */}
        <div className="flex flex-col gap-4 min-w-[200px]">
          <div className="bg-black/50 p-4 rounded-xl border border-pink-500/30">
            <div className="text-center">
              <div className="text-xl font-bold text-blue-400 mb-2">
                Score: {score}
              </div>
              <div className="text-lg text-purple-400 mb-4">
                Level: {level}
              </div>
              
              <div className="space-y-2">
                <button
                  onClick={resetGame}
                  className="w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors border border-blue-400"
                >
                  {gameOver ? '🔄 Play Again' : '🔄 Reset Game'}
                </button>
                
                <button
                  onClick={toggleMusic}
                  className={`w-full px-4 py-2 font-bold rounded-lg transition-colors border ${
                    isMusicPlaying 
                      ? 'bg-purple-600 hover:bg-purple-700 border-purple-400 text-white' 
                      : 'bg-gray-700 hover:bg-gray-600 border-gray-500 text-gray-300'
                  }`}
                >
                  {isMusicPlaying ? '🎵 Stop Music' : '🎵 Play Music'}
                </button>
              </div>
            </div>
          </div>

          {/* Controls */}
          <div className="bg-black/50 p-4 rounded-xl border border-pink-500/30">
            <h3 className="text-base font-bold text-pink-400 mb-2">
              🎮 Controls
            </h3>
            <div className="text-sm text-gray-300 space-y-1">
              <div>⬅️ Move Left</div>
              <div>➡️ Move Right</div>
              <div>⬇️ Drop Faster</div>
              <div>⬆️ Rotate</div>
            </div>
          </div>

          {/* Next Piece Preview / Features */}
          <div className="bg-black/50 p-4 rounded-xl border border-pink-500/30">
            <h3 className="text-base font-bold text-green-400 mb-2">
              ✨ Features
            </h3>
            <div className="text-sm text-gray-300 space-y-1">
              <div>🎵 Classic Tetris Music</div>
              <div>💫 Smooth Animations</div>
              <div>🌈 Color Blocks</div>
              <div>⚡ Progressive Speed</div>
            </div>
          </div>
        </div>
      </div>

      {/* Game Over Modal */}
      {gameOver && (
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 p-6 sm:p-8 rounded-xl shadow-2xl border border-pink-500/50 max-w-md w-full text-center">
            <h2 className="text-3xl font-bold mb-4 text-pink-400">
              🎮 Game Over!
            </h2>
            <p className="text-xl mb-4 text-white">
              Final Score: <span className="text-blue-400 font-bold">{score}</span>
            </p>
            <p className="text-base mb-6 text-gray-300">
              Level Reached: <span className="text-purple-400 font-bold">{level}</span>
            </p>
            <button
              onClick={resetGame}
              className="w-full px-6 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-bold rounded-lg transition-colors border border-blue-400"
            >
              🚀 Play Again
            </button>
          </div>
        </div>
      )}

      {/* Tetris Theme Music */}
      <audio
        ref={audioRef}
        src="https://res.cloudinary.com/di7ctlowx/video/upload/v1754832622/Tetris_rfr6pz.mp3"
        onError={(e) => {
          console.log("Audio failed to load:", e)
          setIsMusicPlaying(false)
        }}
      />
    </div>
  )
}
